import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist';
import { AGURenderService } from 'agu_app_shell';
import { BoxCss, CssBox, FitZoom, PDF_WORKER_SRC, PdfBox, SameHighlights } from './pdf-page';

export interface PdfHighlight {
    page: number;
    bbox: PdfBox;
}

export type CxPdfViewerMode = 'viewer' | 'thumbnail';

/** One rendered page: its CSS size and the scale that maps PDF points to it. */
interface PageSlot {
    Num: number;
    Width: number;
    Height: number;
    Scale: number;
}

interface PlacedHighlight {
    Page: number;
    Css: CssBox;
}

/** P6:1754-1759 zoom steps `d.zoom * .1`, clamped 0.6 to 1.6 (P6 zoom handler). */
const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.6;
const ZOOM_MAX = 1.6;
/** P6:1747 scrolls the evidence to 60 px below the viewer's top edge. */
const SCROLL_MARGIN_PX = 60;

/**
 * The member's real CV, rendered by pdf.js with the evidence boxes drawn over it (P6:569-575 viewer card, :633 highlight).
 * `pdfjs-dist` is imported dynamically on first use, so it is in neither the Explorer's initial bundle nor the unit tests; its
 * worker is served from assets (`PDF_WORKER_SRC`). The canvas rendering and the scroll are browser-only; the math is `pdf-page.ts`.
 */
@Component({
    standalone: false,
    selector: 'ra-cx-pdf-viewer',
    templateUrl: './cx-pdf-viewer.component.html',
    styleUrl: './cx-pdf-viewer.component.scss',
})
export class CxPdfViewerComponent implements OnChanges, OnDestroy {
    @Input() Url: string | null = null;
    @Input() Highlight: readonly PdfHighlight[] = [];
    @Input() Mode: CxPdfViewerMode = 'viewer';
    /** 0 fits the viewer body (`FitZoom`); the member's zoom buttons override it. */
    @Input() Zoom = 0;
    @Input() FileName = '';
    /** The document could not be loaded (an expired link, say): the page may hand a fresh Url. */
    @Output() readonly LoadFailed = new EventEmitter<void>();

    @ViewChild('body') private body?: ElementRef<HTMLElement>;

    Pages: PageSlot[] = [];
    Placed: PlacedHighlight[] = [];
    Loading = false;
    Failed = false;
    /** The zoom in effect, as a fraction (1 = 100%). */
    CurrentZoom = 1;

    private pdf: PDFDocumentProxy | null = null;
    private loadingTask: PDFDocumentLoadingTask | null = null;
    private placedFor: readonly PdfHighlight[] = [];
    private loadToken = 0;
    /** Bumped by every layout (and load): a layout that finds it changed after an await is stale and stops. */
    private layoutGen = 0;
    private zoomSet = false;
    private seen = false;
    private tasks: RenderTask[] = [];
    private destroyed = false;

    constructor(
        private readonly host: ElementRef<HTMLElement>,
        private readonly cdr: ChangeDetectorRef,
        private readonly render: AGURenderService
    ) {}

    get ZoomPercent(): number { return Math.round(this.CurrentZoom * 100); }
    get IsThumbnail(): boolean { return this.Mode === 'thumbnail'; }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['Url']) {
            void this.Load();
            return;
        }
        if (changes['Zoom'] && this.pdf) void this.Layout();
        else if (changes['Highlight'] && this.pdf && !SameHighlights(this.placedFor, this.Highlight)) this.PlaceAndScroll();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.loadToken += 1;
        this.layoutGen += 1;
        this.CancelRenders();
        void this.loadingTask?.destroy();
        this.loadingTask = null;
        this.pdf = null;
    }

    ZoomBy(direction: -1 | 1): void {
        this.zoomSet = true;
        this.CurrentZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round((this.CurrentZoom + direction * ZOOM_STEP) * 10) / 10));
        void this.Layout();
    }

    /** The one place pdf.js is imported: dynamically, so it stays out of the initial bundle and the unit tests. */
    private async ImportPdfJs(): Promise<typeof import('pdfjs-dist')> {
        return await import('pdfjs-dist');
    }

    /** Loads the document: `getDocument({ url })` with the worker from assets, then lays the pages out. */
    private async Load(): Promise<void> {
        const token = ++this.loadToken;
        this.layoutGen += 1;
        this.CancelRenders();
        void this.loadingTask?.destroy(); // stops an in-flight fetch and destroys the document it was loading
        this.loadingTask = null;
        this.pdf = null;
        this.Pages = [];
        this.Placed = [];
        this.Failed = false;
        if (!this.Url) { this.Loading = false; return; }
        this.Loading = true;
        this.render.Notify();
        try {
            const url = this.Url;
            const pdfjs = await this.ImportPdfJs();
            // A newer Load or ngOnDestroy ran while the library was loading: create nothing (no task, no fetch).
            if (token !== this.loadToken || this.destroyed) return;
            pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
            const task = pdfjs.getDocument({ url });
            this.loadingTask = task;
            const doc = await task.promise;
            if (token !== this.loadToken || this.destroyed) {
                // Superseded while fetching: this task is no longer the viewer's, so end its fetch and document here.
                void task.destroy();
                return;
            }
            this.pdf = doc;
            this.Loading = false;
            await this.Layout();
        } catch {
            if (token !== this.loadToken) return;
            this.Loading = false;
            this.Failed = true;
            this.render.Notify();
            this.LoadFailed.emit();
        }
    }

    /** Sizes every page for the current zoom, paints the canvases, places the highlights and scrolls to the first. */
    private async Layout(): Promise<void> {
        const doc = this.pdf;
        if (!doc) return;
        const gen = ++this.layoutGen;
        this.CancelRenders();
        const count = this.IsThumbnail ? 1 : doc.numPages;
        const pages: PDFPageProxy[] = [];
        for (let n = 1; n <= count; n++) pages.push(await doc.getPage(n));
        if (gen !== this.layoutGen) return;

        const first = pages[0].getViewport({ scale: 1 });
        const availableWidth = this.IsThumbnail ? this.host.nativeElement.clientWidth : this.body?.nativeElement.clientWidth ?? 0;
        if (!this.IsThumbnail) {
            if (!this.zoomSet) this.CurrentZoom = this.Zoom > 0 ? this.Zoom : availableWidth > 0 ? FitZoom(availableWidth) : 1;
        }
        // Width in CSS px: the page at 100% is 760 wide (P6 `.cx-doc`), a thumbnail fills its host.
        const targetWidth = this.IsThumbnail ? availableWidth : 760 * this.CurrentZoom;
        const scale = targetWidth > 0 ? targetWidth / first.width : 1;
        this.Pages = pages.map((p, i) => {
            const vp = p.getViewport({ scale });
            return { Num: i + 1, Width: vp.width, Height: vp.height, Scale: scale };
        });
        this.PlaceHighlights();
        this.cdr.detectChanges();
        this.render.Notify();
        await Promise.all(pages.map((p, i) => this.Paint(p, this.Pages[i], gen)));
        if (gen === this.layoutGen) this.ScrollToFirst();
    }

    private async Paint(page: PDFPageProxy, slot: PageSlot, gen: number): Promise<void> {
        if (gen !== this.layoutGen) return;
        const canvas = this.host.nativeElement.querySelector<HTMLCanvasElement>(`canvas[data-page="${slot.Num}"]`);
        const context = canvas?.getContext('2d');
        if (!canvas || !context) return;
        const ratio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: slot.Scale * ratio });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const task = page.render({ canvasContext: context, viewport });
        this.tasks.push(task);
        // A render cancelled by a newer layout rejects; that layout paints instead.
        await task.promise.catch(() => undefined);
    }

    private PlaceHighlights(): void {
        this.placedFor = this.Highlight;
        this.Placed = this.Highlight.flatMap((h) => {
            const slot = this.Pages.find((p) => p.Num === h.page);
            return slot ? [{ Page: h.page, Css: BoxCss(h.bbox, { width: slot.Width, height: slot.Height }) }] : [];
        });
    }

    /** Runs inside `ngOnChanges`, mid-check: no repaint request from here; the scroll waits for the view to render the boxes. */
    private PlaceAndScroll(): void {
        this.PlaceHighlights();
        queueMicrotask(() => this.ScrollToFirst());
    }

    BoxesOn(page: number): PlacedHighlight[] { return this.Placed.filter((b) => b.Page === page); }

    /** Scrolls the first evidence box into view (P6:1748): smooth only when it is not the first view and motion is allowed. */
    private ScrollToFirst(): void {
        const body = this.body?.nativeElement;
        const el = body?.querySelector<HTMLElement>('.cx-hl');
        if (!body || !el) return;
        const top = Math.max(0, el.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - SCROLL_MARGIN_PX);
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.seen && !reduced) body.scrollTo({ top, behavior: 'smooth' });
        else body.scrollTop = top;
        this.seen = true;
    }

    private CancelRenders(): void {
        for (const t of this.tasks) t.cancel();
        this.tasks = [];
    }
}
